import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, PageLoader, StatusBadge, EmptyState, Btn, TextInput, TextArea } from "@/lib/ui";
import { Mails, Plus, Trash2, Send, Users, FileText, TestTube2, PencilLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CAMP_TONE = { draft: "muted", sent: "ok" };

function SubscriberDialog({ open, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: "", name: "", brand: "" });
  useEffect(() => { if (open) setForm({ email: "", name: "", brand: "" }); }, [open]);
  const add = useMutation({
    mutationFn: () => api.post("/cms/newsletter/subscribers", form),
    onSuccess: () => {
      toast.success("Subscriber added");
      qc.invalidateQueries({ queryKey: ["nl-subscribers"] });
      qc.invalidateQueries({ queryKey: ["nl-stats"] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-md">
        <DialogHeader><DialogTitle>Add subscriber</DialogTitle><DialogDescription>Manually add someone to the newsletter list.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <TextInput label="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="nl-sub-email" />
          <TextInput label="Name (optional)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="nl-sub-name" />
          <Btn onClick={() => add.mutate()} disabled={add.isPending || !form.email.trim()} data-testid="nl-sub-save">{add.isPending ? "Adding…" : "Add subscriber"}</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Subscribers() {
  const api = useApi();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const { data: stats } = useQuery({ queryKey: ["nl-stats"], queryFn: () => api.get("/cms/newsletter/stats") });
  const { data: subs = [], isLoading } = useQuery({
    queryKey: ["nl-subscribers", filter],
    queryFn: () => api.get(`/cms/newsletter/subscribers${filter === "all" ? "" : `?status=${filter}`}`),
  });
  const del = useMutation({
    mutationFn: (id) => api.del(`/cms/newsletter/subscribers/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["nl-subscribers"] }); qc.invalidateQueries({ queryKey: ["nl-stats"] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div data-testid="nl-subscribers">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          {["all", "subscribed", "unsubscribed"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`nl-filter-${s}`}
              className={cn("text-xs rounded-full px-3 py-1.5 border transition-colors capitalize",
                filter === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground")}>
              {s}{stats ? ` · ${s === "all" ? (stats.total ?? 0) : (stats[s] ?? 0)}` : ""}
            </button>
          ))}
        </div>
        <Btn size="sm" onClick={() => setAdding(true)} data-testid="nl-add-subscriber"><Plus className="h-4 w-4" /> Add subscriber</Btn>
      </div>
      {isLoading ? <PageLoader /> : subs.length === 0 ? (
        <Panel className="p-10"><EmptyState icon={Users} title="No subscribers yet" hint="Subscribers from the website signup form and manual adds appear here." /></Panel>
      ) : (
        <Panel className="divide-y divide-border/60" data-testid="nl-subscriber-list">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`nl-subscriber-${s.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.email}</div>
                <div className="text-xs text-muted-foreground truncate">{s.name || "—"} · via {s.source || "website"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge tone={s.status === "subscribed" ? "ok" : "muted"} dot>{s.status}</StatusBadge>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Btn variant="ghost" size="icon" title="Remove" data-testid={`nl-sub-del-${s.id}`}><Trash2 className="h-4 w-4" /></Btn></AlertDialogTrigger>
                  <AlertDialogContent className="bg-surface border-border">
                    <AlertDialogHeader><AlertDialogTitle>Remove subscriber?</AlertDialogTitle><AlertDialogDescription>This permanently removes {s.email} from the list.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del.mutate(s.id)} className="bg-err text-white hover:bg-err/90">Remove</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </Panel>
      )}
      <SubscriberDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function CampaignEditor({ item, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const isNew = !item?.id;
  const sent = item?.status === "sent";
  const [form, setForm] = useState({ subject: "", body: "" });
  const [testEmail, setTestEmail] = useState("");
  useEffect(() => { setForm({ subject: item?.subject || "", body: item?.body || "" }); }, [item?.id]); // eslint-disable-line
  const inval = () => { qc.invalidateQueries({ queryKey: ["nl-campaigns"] }); qc.invalidateQueries({ queryKey: ["nl-stats"] }); };

  const save = useMutation({
    mutationFn: () => isNew ? api.post("/cms/newsletter/campaigns", form) : api.patch(`/cms/newsletter/campaigns/${item.id}`, form),
    onSuccess: () => { toast.success(isNew ? "Draft created" : "Saved"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const test = useMutation({
    mutationFn: () => api.post(`/cms/newsletter/campaigns/${item.id}/test`, { email: testEmail }),
    onSuccess: (d) => toast.success(`Test sent to ${d.sent_to} (${d.status})`),
    onError: (e) => toast.error(e.message),
  });
  const send = useMutation({
    mutationFn: () => api.post(`/cms/newsletter/campaigns/${item.id}/send`),
    onSuccess: (d) => { toast.success(`Sent to ${d.sent} subscriber(s)${d.failed ? ` · ${d.failed} failed` : ""}`); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="nl-campaign-editor">
      <TextInput label="Subject" value={form.subject} disabled={sent} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="nl-camp-subject" />
      <div>
        <span className="text-xs text-muted-foreground mb-1.5 block">Body (HTML supported)</span>
        <TextArea rows={10} value={form.body} disabled={sent} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="<p>Hello…</p>" data-testid="nl-camp-body" />
      </div>
      {!sent && (
        <div className="flex items-center gap-2 border-t border-border pt-4">
          <Btn onClick={() => save.mutate()} disabled={save.isPending || !form.subject.trim()} data-testid="nl-camp-save">{save.isPending ? "Saving…" : isNew ? "Create draft" : "Save"}</Btn>
        </div>
      )}
      {!isNew && !sent && (
        <div className="space-y-3 border-t border-border pt-4">
          <div className="flex items-end gap-2">
            <TextInput label="Send a test to" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="flex-1" data-testid="nl-camp-test-email" />
            <Btn variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !testEmail.trim()} data-testid="nl-camp-test-btn"><TestTube2 className="h-4 w-4" /> Test</Btn>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild><Btn className="w-full" data-testid="nl-camp-send-btn"><Send className="h-4 w-4" /> Send to all subscribers</Btn></AlertDialogTrigger>
            <AlertDialogContent className="bg-surface border-border">
              <AlertDialogHeader><AlertDialogTitle>Send this campaign now?</AlertDialogTitle><AlertDialogDescription>This emails “{form.subject}” to every subscribed contact. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => send.mutate()} disabled={send.isPending} data-testid="nl-camp-send-confirm">{send.isPending ? "Sending…" : "Send now"}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
      {sent && <div className="text-xs text-muted-foreground border-t border-border pt-4">Sent {item.sent_at?.slice(0, 10)} · {item.recipients_count} recipient(s). Sent campaigns are read-only.</div>}
    </div>
  );
}

function Campaigns() {
  const api = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: camps = [], isLoading } = useQuery({ queryKey: ["nl-campaigns"], queryFn: () => api.get("/cms/newsletter/campaigns") });
  const del = useMutation({
    mutationFn: (id) => api.del(`/cms/newsletter/campaigns/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["nl-campaigns"] }); qc.invalidateQueries({ queryKey: ["nl-stats"] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div data-testid="nl-campaigns">
      <div className="flex items-center justify-end mb-4">
        <Btn size="sm" onClick={() => setEditing({})} data-testid="nl-new-campaign"><Plus className="h-4 w-4" /> New campaign</Btn>
      </div>
      {isLoading ? <PageLoader /> : camps.length === 0 ? (
        <Panel className="p-10"><EmptyState icon={FileText} title="No campaigns yet" hint="Draft a newsletter and send it to your subscribers."
          action={<Btn size="sm" className="mt-2" onClick={() => setEditing({})} data-testid="nl-empty-new"><Plus className="h-4 w-4" /> New campaign</Btn>} /></Panel>
      ) : (
        <Panel className="divide-y divide-border/60" data-testid="nl-campaign-list">
          {camps.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`nl-campaign-${c.id}`}>
              <button onClick={() => setEditing(c)} className="min-w-0 text-left flex-1" data-testid={`nl-camp-open-${c.id}`}>
                <div className="text-sm font-medium truncate">{c.subject}</div>
                <div className="text-xs text-muted-foreground">{c.status === "sent" ? `Sent ${c.sent_at?.slice(0, 10)} · ${c.recipients_count} recipient(s)` : `Draft · ${c.created_at?.slice(0, 10)}`}</div>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge tone={CAMP_TONE[c.status]} dot>{c.status}</StatusBadge>
                <Btn variant="ghost" size="icon" title="Edit" onClick={() => setEditing(c)} data-testid={`nl-camp-edit-${c.id}`}><PencilLine className="h-4 w-4" /></Btn>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Btn variant="ghost" size="icon" title="Delete" data-testid={`nl-camp-del-${c.id}`}><Trash2 className="h-4 w-4" /></Btn></AlertDialogTrigger>
                  <AlertDialogContent className="bg-surface border-border">
                    <AlertDialogHeader><AlertDialogTitle>Delete campaign?</AlertDialogTitle><AlertDialogDescription>This removes “{c.subject}”. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del.mutate(c.id)} className="bg-err text-white hover:bg-err/90">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </Panel>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? (editing.status === "sent" ? "Campaign" : "Edit campaign") : "New campaign"}</DialogTitle><DialogDescription>Compose and send a newsletter to your subscribers.</DialogDescription></DialogHeader>
          {editing && <CampaignEditor item={editing} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Newsletter() {
  return (
    <div data-testid="newsletter-page">
      <PageHeader kicker="Content · Website CMS" title="Newsletter">
        <Mails className="h-5 w-5 text-primary" />
      </PageHeader>
      <Tabs defaultValue="subscribers">
        <TabsList data-testid="nl-tabs">
          <TabsTrigger value="subscribers" data-testid="nl-tab-subscribers"><Users className="h-4 w-4 mr-1.5" /> Subscribers</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="nl-tab-campaigns"><FileText className="h-4 w-4 mr-1.5" /> Campaigns</TabsTrigger>
        </TabsList>
        <TabsContent value="subscribers" className="mt-4"><Subscribers /></TabsContent>
        <TabsContent value="campaigns" className="mt-4"><Campaigns /></TabsContent>
      </Tabs>
    </div>
  );
}
